import React from 'react';
import Link from 'next/link';
import styled from 'styled-components';
import Imgix from 'shared/components/imgix';
import { KIOSK_PRO_MEDIA } from 'src/components/kiosk-pro/kiosk-pro-shared/kiosk-pro-shared.styles';
import { NoLogoSvg } from './no-logo-svg';

type Brand = {
  id: string;
  name: string;
  imageUrl?: string;
  description?: string;
};

type BrandCardProps = {
  brand: Brand;
  href: string;
  onClick?: () => void;
};

export const BrandCard = React.memo(
  ({ brand, href, onClick }: BrandCardProps): JSX.Element => {
    const handleClick = (): void => {
      onClick?.();
    };

    return (
      <StyledLink href={href} onClick={handleClick}>
        <StyledCard href={href} onClick={handleClick} data-testid={`brand-card-${brand.id}`}>
          <ImageWrapper>
            {brand.imageUrl ? (
              <BrandImage src={brand.imageUrl} alt={brand.name} />
            ) : (
              <PlaceholderImage>
                <NoLogoSvg width={230} height={230} />
                <PlaceholderLetter>{brand.name.charAt(0).toUpperCase()}</PlaceholderLetter>
              </PlaceholderImage>
            )}
          </ImageWrapper>

          <ContentWrapper>
            <BrandName>{brand.name}</BrandName>
          </ContentWrapper>
        </StyledCard>
      </StyledLink>
    );
  }
);

const StyledLink = styled(Link)`
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  user-select: none;
`;

const StyledCard = styled.a`
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  border: 1px solid #e3e7e9;
  background: #fff;
  box-shadow: 0 0 0 rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  cursor: pointer;
  overflow: hidden;
  text-align: center;
  overflow: hidden;
  padding: 16px;
  gap: 8px;

  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }
`;

const ImageWrapper = styled.div`
  width: 100%;
  height: 180px;
  position: relative;
  overflow: hidden;

  @media (max-width: 720px) {
    height: 120px;

    svg {
      width: 140px;
      height: 140px;
    }
  }
`;

const BrandImage = styled(Imgix)`
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
`;

const PlaceholderImage = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  svg {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1;
  }
`;

const ContentWrapper = styled.div`
  padding-bottom: 20px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  justify-content: center;

  @media (max-width: 720px) {
    padding: 0;
  }
`;

const BrandName = styled.h3`
  font-size: 24px;
  font-weight: 600;
  padding: 0;

  @media ${KIOSK_PRO_MEDIA.landscapeMaxWidth} {
    font-size: 18px;
  }

  @media ${KIOSK_PRO_MEDIA.portraitMaxWidth} {
    font-size: 18px;
  }
`;

const PlaceholderLetter = styled.div`
  font-size: 56px;
  font-weight: bold;
  color: #485055;
  position: relative;
  z-index: 2;

  @media (max-width: 720px) {
    font-size: 40px;
  }
`;
